/* eslint-disable import/no-unassigned-import */

import moment from 'moment';
import {DateTimeFormat} from 'common/proxies/enum-proxy';

// Include specific Moment locales. Note that the
// locales not listed here are explicitly ignored
// by Webpack's IgnorePlugin, because including all
// Moment locales results in heavy bundles indeed.
// The included Moment locale bundles are:
// - German (de)
// - British English (en-gb)
// - Spanish (es)
// - Norwegian (nb)
// - Mandarin (zh-cn)
import 'moment/locale/de';
import 'moment/locale/en-gb';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/fr-ca';
import 'moment/locale/ja';
import 'moment/locale/nb';
import 'moment/locale/zh-cn';

// Grab locale code from window.settings.locale and
// set on Moment. All Moment operations, globally,
// including in templated JS, should now occur in
// the passed locale.
if (window && window.settings && window.settings.locale) {
  let locale = window.settings.locale;
  switch (locale) {
    case 'nb_NO':
      locale = 'nb';
      break;
    case 'fr_FR':
      locale = 'fr';
      break;
    case 'ja_JP':
      locale = 'ja';
      break;
    default:
      break;
  }
  moment.locale(locale);
} else {
  moment.locale('en_US');
}

// Valid formats for dates and times. If a formatting
// string passed to formatDate() is absent from this
// list, an error will be thrown.
const VALID_FORMATS = Object.values(DateTimeFormat)
  .map((format) => format.moment)
  .filter((item) => item);

// Core date formatting. All date/time formatting in
// JS should happen via this function.
// The value of the date parameter gets passed through
// to Moment, so it can be anything that Moment is capable
// of parsing: https://momentjs.com/docs/#/parsing/
// The format parameter can be a string containing
// any of the formats in VALID_FORMATS. That is,
// any member of DateTimeFormat whose "moment"
// attribute contains a valid formatting string.
// If formatDate() receives a formatting string
// not enumerated in DateTimeFormat, it will
// explode and write instructions to the console.
export const formatDate = (date, format, time = true) => {
  if (!format || !VALID_FORMATS.includes(format.moment)) {
    const examples = Object.keys(DateTimeFormat).map((item) => {
      const example = moment().format(DateTimeFormat[item].moment);
      return `DateTimeFormat.${item}.moment => ${example}`;
    });

    throw new Error(`\n
      The specified date/time format is not permitted: "${format}"

      Permitted formats in JS are:

      ${examples.sort().join('\n      ')}

      Please see:
      https://ablcode.visualstudio.com/Lucid/_wiki/wikis/Lucid.wiki/22578/i18n-Internationalization
    `);
  }

  // use this workaround described in moment's GitHub issue (https://github.com/moment/moment/issues/2505)
  // until moment adds format for a full date without time
  if (DateTimeFormat.TEXTUAL_DAY_OF_WEEK === format && !time) {
    const llll = moment(date).format(DateTimeFormat.TEXTUAL_DAY_OF_WEEK.moment);
    const lll = moment(date).format('lll');
    const ll = moment(date).format(DateTimeFormat.TEXTUAL.moment);
    return llll.replace(lll.replace(ll, ''), '');
  }
  return moment(date).format(format.moment);
};

/* Convenience function for formatting ranges. */
export const formatDateRange = (start, end, startFormat, endFormat) => {
  const startFormatted = formatDate(start, startFormat);
  const endFormatted = formatDate(end, endFormat || startFormat);
  return `${startFormatted} - ${endFormatted}`;
};

/* Legacy formatting for templated dates. */
export const localtime = () => {
  const elements = $('cite.localtime');
  elements.each((index, el) => {
    const date = new Date(el.innerHTML);
    const localDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60 * 1000,
    );

    const formatMoment = el.getAttribute('format');
    const dateTimeFormat =
      Object.values(DateTimeFormat).find(
        (format) => format.moment === formatMoment,
      ) || DateTimeFormat.TEXTUAL_DAY_OF_WEEK;
    el.innerHTML = formatDate(localDate, dateTimeFormat);
  });
  elements.removeClass('localtime');
};
